// RODetailPage.jsx - Complete single-page Repair Order view with all sections
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { RO_STATUSES, APPROVAL_THRESHOLD, INTERNAL_RATE, LABOR_RATE, MARKUP_RATE, VENDOR_TYPES } from './constants';
import { StatusBadge, SectionHeader, FileUpload, PhotoGallery, LoadingSpinner, ErrorDisplay } from './shared';
import { WorkItemsSection, TimelineSection } from './ROSections';
import { VehicleFilesButton } from '../VehicleFilesModal';
import CustomerInvoice from './CustomerInvoice';
import VinDisplay from '../shared/VinDisplay';

const RODetailPage = ({ roId, users, vendors, onClose, showCosts }) => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  
  const [ro, setRO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [showAddPart, setShowAddPart] = useState(false);
  const [newPart, setNewPart] = useState({
    description: '',
    partNumber: '',
    quantity: 1,
    unitCost: '',
    vendorName: '',
    status: 'ordered',
    orderDate: new Date().toISOString().split('T')[0],
    eta: '',
    bankAccountId: '',
    notes: '',
    // Labor fields
    laborHours: '',
    laborRate: '35',
    materialsCost: '',
    materialsDescription: '',
    isBundledJob: false
  });
  const [addingPart, setAddingPart] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const saveTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);
  const justSavedRef = useRef(false);
  
  // Additional labor state
  const [showAddLabor, setShowAddLabor] = useState(false);
  const [newLabor, setNewLabor] = useState({ description: '', hours: '' });
  const [addingLabor, setAddingLabor] = useState(false);
  
  // Customer invoice state
  const [showCustomerInvoice, setShowCustomerInvoice] = useState(false);

  // Cancelled parts visibility toggle
  const [showCancelledParts, setShowCancelledParts] = useState(false);

  // Debug: Log when showCustomerInvoice changes
  useEffect(() => {
    console.log('showCustomerInvoice state changed to:', showCustomerInvoice);
  }, [showCustomerInvoice]);

  // Load bank accounts
  useEffect(() => {
    api.getBankAccounts().then(setBankAccounts).catch(console.error);
  }, []);

  // Load RO data
  const loadRO = useCallback(async () => {
    if (!roId) {
      console.error('RODetailPage: No roId provided');
      setError('No repair order ID provided');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      console.log('RODetailPage: Loading RO with ID:', roId);
      const roData = await api.getRepairOrder(roId);
      console.log('RODetailPage: RO data received:', roData);
      
      if (!roData || !roData.id) {
        throw new Error('Invalid RO data received');
      }
      
      // Manually fetch vehicle data to ensure inspection_notes is included
      if (roData.vehicleId) {
        try {
          const vehicleData = await api.getVehicle(roData.vehicleId);
          roData.vehicle = vehicleData;
        } catch (vehicleErr) {
          console.warn('Failed to load vehicle data:', vehicleErr);
        }
      }
      console.log('RO Data Loaded:', roData);
      console.log('[DEBUG] estimateOnly value from API:', roData.estimateOnly, typeof roData.estimateOnly);
      setRO(roData);
      setError(null);
    } catch (err) {
      console.error('Failed to load RO:', err);
      setError('Failed to load repair order: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [roId]);

  useEffect(() => {
    if (justSavedRef.current) {
      console.log('[useEffect] Skipping loadRO - just saved, will reload after delay');
      return;
    }
    console.log('[useEffect] loadRO triggered, roId:', roId);
    loadRO();
  }, [loadRO, roId]);

  // Auto-refresh polling every 30 seconds to catch approval updates
  useEffect(() => {
    const pollInterval = setInterval(() => {
      // Only poll if RO is pending approval - to catch when admin approves
      if (ro?.status === 'pending_approval' || ro?.status === 'submitted') {
        loadRO();
      }
    }, 30000); // 30 seconds
    
    return () => clearInterval(pollInterval);
  }, [ro?.status, loadRO]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleSyncPrecheckParts = async () => {
    if (!ro?.id) return;
    console.log(`Calling migrate-parts for RO ID: ${ro.id}`); // Step 1: Verify button action
    try {
      const response = await api.post(`/repair-orders/${ro.id}/migrate-parts`);
      alert(response.message || 'Successfully synced pre-check parts.');
      loadRO(); // Refresh the RO details
    } catch (error) {
      console.error('Failed to sync precheck parts:', error);
      alert(error.message || 'Failed to sync pre-check parts.');
    }
  };

  // Debounced auto-save function
  const debouncedSave = useCallback(async (updates) => {
    // Prevent re-entry
    if (isSavingRef.current) {
      console.log('[SAVE] Already saving, skipping...');
      return;
    }
    
    isSavingRef.current = true;
    setSaving(true);
    setSaved(false);
    
    try {
      const result = await api.updateRepairOrder(roId, updates);
      
      // NOW update local state AFTER API succeeds
      setRO(prev => {
        return prev ? { ...prev, ...updates } : prev;
      });
      
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);

      // Refresh once to reflect server-calculated totals/derived fields.
      await loadRO();
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
      isSavingRef.current = false;
    }
  }, [roId, loadRO]);

  // Autosave every 10 seconds if there are pending changes
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (Object.keys(pendingChanges).length > 0 && !saving) {
        console.log('[RO AutoSave] Saving pending changes...');
        debouncedSave(pendingChanges);
        setPendingChanges({});
      }
    }, 10000);

    return () => clearInterval(autoSaveInterval);
  }, [pendingChanges, saving, debouncedSave]);

  // Update RO field with debounce (auto-save after 1.5 seconds of inactivity)
  const updateRO = useCallback((updates, immediate = false) => {
    console.log('[updateRO] Called with updates:', updates, 'immediate:', immediate);
    // Merge with pending changes
    const newPending = { ...pendingChanges, ...updates };
    setPendingChanges(newPending);
    
    // DO NOT update local state here - wait for API to succeed
    // This prevents the useEffect loop
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    if (immediate) {
      // Save immediately
      console.log('[updateRO] Saving immediately...');
      debouncedSave(newPending);
      setPendingChanges({});
    } else {
      // Debounce save
      saveTimeoutRef.current = setTimeout(() => {
        console.log('[updateRO] Debounced save triggered...');
        debouncedSave(newPending);
        setPendingChanges({});
      }, 1500);
    }
  }, [pendingChanges, debouncedSave]);

  // Manual save button
  const handleManualSave = async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    if (Object.keys(pendingChanges).length > 0) {
      await debouncedSave(pendingChanges);
      setPendingChanges({});
    } else {
      // Just show saved indicator
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  // Handle file uploads
  const handleUpload = async (type, url) => {
    try {
      const updates = {};
      if (type === 'video') {
        updates.inspectionVideoUrl = url;
      } else if (type === 'scan') {
        updates.bluedriverScanUrl = url;
      } else if (type === 'photo') {
        updates.photos = [...(ro.photos || []), url];
      }
      await api.updateROUploads(roId, updates);
      await loadRO();
    } catch (err) {
      alert('Failed to upload: ' + err.message);
    }
  };

  // Handle photo delete
  const handlePhotoDelete = async (index) => {
    try {
      const newPhotos = (ro?.photos || []).filter((_, i) => i !== index);
      await api.updateROUploads(roId, { photos: newPhotos });
      await loadRO();
    } catch (err) {
      alert('Failed to delete photo: ' + err.message);
    }
  };

  // Print RO
  const handlePrint = () => {
    window.print();
  };

  // Parts management functions
  const handleAddPart = async () => {
    if (!newPart.description.trim()) {
      alert('Part/Job description is required');
      return;
    }
    const qty = parseInt(newPart.quantity) || 1;
    const cost = parseFloat(newPart.unitCost) || 0;
    const payload = {
      description: newPart.description,
      partNumber: newPart.partNumber,
      quantity: qty,
      unitCost: cost,
      vendorName: newPart.vendorName,
      status: newPart.status || 'ordered',
      orderDate: newPart.orderDate || null,
      eta: newPart.eta || null,
      accountCode: newPart.accountCode || null,
      accountName: newPart.accountName || null,
      notes: newPart.notes || null,
      laborHours: parseFloat(newPart.laborHours) || 0,
      laborRate: parseFloat(newPart.laborRate) || 35,
      materialsCost: parseFloat(newPart.materialsCost) || 0,
      materialsDescription: newPart.materialsDescription || null,
      isBundledJob: newPart.isBundledJob || false
    };
    const pendingId = 'pending-' + Date.now();
    const pendingPart = {
      id: pendingId,
      description: newPart.description,
      partNumber: newPart.partNumber,
      quantity: qty,
      unitCost: cost,
      totalCost: qty * cost,
      vendorName: newPart.vendorName,
      status: newPart.status || 'ordered',
      orderDate: newPart.orderDate || null,
      eta: newPart.eta || null,
      notes: newPart.notes || null,
      _pending: true
    };
    // Optimistic update - show part immediately so UI doesn't feel frozen
    setRO(prev => prev ? { ...prev, parts: [...(prev.parts || []), pendingPart] } : prev);
    setNewPart({
      description: '', partNumber: '', quantity: 1, unitCost: '', vendorName: '',
      status: 'ordered', orderDate: new Date().toISOString().split('T')[0], eta: '',
      accountCode: '', accountName: '', notes: '', laborHours: '', laborRate: '35',
      materialsCost: '', materialsDescription: '', isBundledJob: false
    });
    setShowAddPart(false);
    setAddingPart(false);
    try {
      await api.addRepairOrderPart(roId, payload);
      await loadRO();
    } catch (error) {
      console.error('Failed to add part:', error);
      setRO(prev => prev ? { ...prev, parts: (prev.parts || []).filter(p => p.id !== pendingId) } : prev);
      alert('Failed to add part: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeletePart = async (partId) => {
    if (String(partId).startsWith('pending-')) return;
    if (!window.confirm('Delete this part?')) return;
    try {
      await api.deleteRepairOrderPart(roId, partId);
      await loadRO();
    } catch (error) {
      console.error('Failed to delete part:', error);
      alert('Failed to delete part');
    }
  };

  // Cancel part (soft delete - keeps data, removes from costs)
  const handleCancelPart = async (partId, partDescription) => {
    const reason = window.prompt(`Cancel part "${partDescription}"?\n\nOptionally enter a reason:`, '');
    if (reason === null) return; // User clicked Cancel
    try {
      setSaving(true);
      await api.cancelROPart(roId, partId, reason || null);
      await loadRO();
    } catch (error) {
      console.error('Failed to cancel part:', error);
      alert('Failed to cancel part: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // Restore a cancelled part
  const handleRestorePart = async (partId) => {
    if (String(partId).startsWith('pending-')) return;
    if (!window.confirm('Restore this cancelled part?')) return;
    try {
      setSaving(true);
      await api.restoreROPart(roId, partId);
      await loadRO();
    } catch (error) {
      console.error('Failed to restore part:', error);
      alert('Failed to restore part: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePart = async (partId, updates) => {
    if (String(partId).startsWith('pending-')) return;
    try {
      await api.updateRepairOrderPart(roId, partId, updates);
      await loadRO();
    } catch (error) {
      console.error('Failed to update part:', error);
      alert('Failed to update part');
    }
  };

  // Handle adding additional labor (not tied to parts)
  const handleAddLabor = async () => {
    if (!newLabor.description.trim()) {
      alert('Labor description is required');
      return;
    }
    if (!newLabor.hours || parseFloat(newLabor.hours) <= 0) {
      alert('Hours must be greater than 0');
      return;
    }
    setAddingLabor(true);
    try {
      // Add as a "labor only" part entry with no part cost
      await api.addRepairOrderPart(roId, {
        description: `[LABOR] ${newLabor.description}`,
        partNumber: null,
        quantity: 1,
        unitCost: 0,
        vendorName: null,
        status: 'installed',
        laborHours: parseFloat(newLabor.hours) || 0,
        laborRate: 35,
        materialsCost: 0,
        isBundledJob: false
      });
      setNewLabor({ description: '', hours: '' });
      setShowAddLabor(false);
      await loadRO();
    } catch (error) {
      console.error('Failed to add labor:', error);
      alert('Failed to add labor: ' + (error.message || 'Unknown error'));
    } finally {
      setAddingLabor(false);
    }
  };

  // Customer Invoice - EARLY RETURN when showing invoice (guaranteed to render)
  if (showCustomerInvoice) {
    return (
      <CustomerInvoice
        roId={roId}
        onClose={() => setShowCustomerInvoice(false)}
        onSave={() => {
          setShowCustomerInvoice(false);
          loadRO();
        }}
      />
    );
  }

  // Loading state
  if (loading) {
    return <LoadingSpinner message="Loading repair order..." />;
  }

  // Error state
  if (error || !ro) {
    return <ErrorDisplay message={error || 'Repair order not found'} />;
  }

  // Safety check for required RO properties
  if (!ro.roNumber) {
    console.error('RO missing roNumber:', ro);
    return <ErrorDisplay message="Invalid repair order data" />;
  }

  // Get shop users for assignment dropdown
  const shopUsers = users?.filter(u => u.role === 'shop' || u.role === 'admin') || [];

  // Get parts from RO - ensure it's always an array
  const parts = Array.isArray(ro.parts) ? ro.parts : [];

  // Calculate costs - labor and materials now come from parts (with safe defaults)
  const partsCost = parts.reduce((sum, p) => sum + (parseFloat(p?.totalCost) || 0), 0);
  const laborCost = parts.reduce((sum, p) => sum + (parseFloat(p?.laborCost) || 0), 0);
  const materialsCost = parts.reduce((sum, p) => sum + (parseFloat(p?.materialsCost) || 0), 0);
  const totalLaborHours = parts.reduce((sum, p) => sum + (parseFloat(p?.laborHours) || 0), 0);
  const outsideVendors = Array.isArray(ro.outsideVendors) ? ro.outsideVendors : [];
  const vendorsCost = outsideVendors.reduce((sum, v) => sum + (parseFloat(v?.actualCost || v?.estimatedCost) || 0), 0);
  const grandTotal = partsCost + laborCost + materialsCost + vendorsCost;
  const requiresApproval = grandTotal > APPROVAL_THRESHOLD;

  // Committed totals exclude estimate-only lines (isEstimate=true)
  const committedParts = parts.filter(p => !p?.isEstimate);
  const committedOutsideVendors = outsideVendors.filter(v => !v?.isEstimate);
  const committedPartsCost = committedParts.reduce((sum, p) => sum + (parseFloat(p?.totalCost) || 0), 0);
  const committedLaborCost = committedParts.reduce((sum, p) => sum + (parseFloat(p?.laborCost) || 0), 0);
  const committedMaterialsCost = committedParts.reduce((sum, p) => sum + (parseFloat(p?.materialsCost) || 0), 0);
  const committedVendorsCost = committedOutsideVendors.reduce((sum, v) => sum + (parseFloat(v?.actualCost || v?.estimatedCost) || 0), 0);
  const committedGrandTotal = committedPartsCost + committedLaborCost + committedMaterialsCost + committedVendorsCost;

  const handleToggleEstimateOnly = async (enabled) => {
    console.log('[EstimateOnly] Toggle called with enabled =', enabled, 'ro.id =', ro?.id);
    if (!ro?.id) {
      console.log('[EstimateOnly] No ro.id, returning early');
      return;
    }
    if (enabled === false) {
      const ok = window.confirm(
        'Turn OFF Estimate Only?\n\nThis will COMMIT all estimate line items so they count toward RO totals and vehicle recon cost.'
      );
      if (!ok) {
        console.log('[EstimateOnly] User cancelled confirmation');
        return;
      }
    }
    try {
      console.log('[EstimateOnly] Calling API...');
      setSaving(true);
      const result = await api.setRepairOrderEstimateOnly(ro.id, enabled);
      console.log('[EstimateOnly] API returned:', result);
      await loadRO();
      console.log('[EstimateOnly] RO reloaded');
    } catch (err) {
      console.error('[EstimateOnly] API error:', err);
      alert('Failed to toggle Estimate Only: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 print:space-y-4">
      {/* ============ HEADER ============ */}
      <div className="flex items-center gap-4 print:hidden">
        <button
          onClick={onClose}
          className="p-2 hover:bg-neutral-100 rounded-lg text-xl transition"
          title="Back to Dashboard"
        >
          ←
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold font-mono">{ro.roNumber}</h1>
            <StatusBadge status={ro.status} size="lg" />
            {ro.priority === 'high' && (
              <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded font-medium">HIGH</span>
            )}
            {ro.priority === 'urgent' && (
              <span className="px-2 py-0.5 text-xs bg-red-600 text-white rounded font-medium animate-pulse">URGENT</span>
            )}
            {ro.estimateOnly && (
              <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded font-medium">ESTIMATE ONLY</span>
            )}
          </div>
          <p className="text-neutral-500 flex items-center gap-2">
            {ro.stockNumber} - {ro.vehicleYear} {ro.vehicleMake} {ro.vehicleModel}
            {ro.vehicleVin && <VinDisplay vin={ro.vehicleVin} compact className="ml-2" />}
          </p>
        </div>
        <button
          onClick={handleManualSave}
          disabled={saving}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
        >
          💾 Save
        </button>
        <VehicleFilesButton 
          vehicleId={ro.vehicleId} 
          roId={roId}
          vehicle={{ stockNumber: ro.stockNumber, year: ro.vehicleYear, make: ro.vehicleMake, model: ro.vehicleModel }}
          className="px-3 py-1.5 text-sm"
        />
        <button
          onClick={() => window.open(window.location.origin + window.location.pathname + '#partsroom', '_blank')}
          className="px-3 py-1.5 border border-neutral-200 rounded-lg text-sm hover:bg-neutral-50 flex items-center gap-1"
          title="Open Parts Room"
        >
          📦 Parts Room
        </button>
        <button
          onClick={handlePrint}
          className="px-3 py-1.5 border border-neutral-200 rounded-lg text-sm hover:bg-neutral-50 flex items-center gap-1"
        >
          🖨️ Print RO
        </button>
        {ro.isCustomerVehicle && (
          <button
            onClick={() => {
              console.log('Customer Invoice button clicked, setting showCustomerInvoice to true');
              setShowCustomerInvoice(true);
            }}
            className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 flex items-center gap-1"
          >
            🧾 Customer Invoice
          </button>
        )}
        {ro.status !== 'complete' && ro.status !== 'closed' && (
          <button
            onClick={() => {
              if (window.confirm('Mark this RO as complete?')) {
                updateRO({ status: 'complete' });
              }
            }}
            className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-1"
          >
            ✓ Mark Complete
          </button>
        )}
        {ro.status === 'complete' && (
          <span className="px-4 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium flex items-center gap-1">
            ✓ Completed
          </span>
        )}
        {saving && (
          <span className="text-sm text-blue-600 flex items-center gap-1">
            <span className="animate-pulse">●</span> Saving...
          </span>
        )}
        {saved && !saving && (
          <span className="text-sm text-green-600 flex items-center gap-1">
            ✓ Saved
          </span>
        )}
      </div>

      {/* ============ PROFESSIONAL PRINT WORK ORDER ============ */}
      <div className="hidden print:block">
        {/* Header */}
        <div className="flex justify-between items-start border-b-2 border-black pb-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">REPAIR ORDER</h1>
            <p className="text-sm text-gray-600">The Wholesale Cars</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold font-mono">{ro.roNumber}</div>
            <div className="text-sm text-gray-600">Date: {new Date(ro.createdAt).toLocaleDateString()}</div>
          </div>
        </div>

        {/* Vehicle Info Box */}
        <div className="border-2 border-black p-3 mb-4">
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div className="flex">
              <span className="font-bold w-24">Stock #:</span>
              <span className="font-mono">{ro.stockNumber}</span>
            </div>
            <div className="flex">
              <span className="font-bold w-24">RO #:</span>
              <span className="font-mono">{ro.roNumber}</span>
            </div>
            <div className="flex">
              <span className="font-bold w-24">Vehicle:</span>
              <span>{ro.vehicleYear} {ro.vehicleMake} {ro.vehicleModel}</span>
            </div>
            <div className="flex">
              <span className="font-bold w-24">Mileage:</span>
              <span>{ro.mileage ? ro.mileage.toLocaleString() : '-'}</span>
            </div>
            <div className="flex col-span-2">
              <span className="font-bold w-24">VIN:</span>
              <VinDisplay vin={ro.vin} />
            </div>
            <div className="flex">
              <span className="font-bold w-24">Status:</span>
              <span className="uppercase">{ro.status?.replace(/_/g, ' ')}</span>
            </div>
            <div className="flex">
              <span className="font-bold w-24">Assigned:</span>
              <span>{ro.assignedToName || 'Unassigned'}</span>
            </div>
          </div>
        </div>

        {/* Work Items Section */}
        {ro.workItems?.length > 0 && (
          <div className="mb-4">
            <h2 className="font-bold text-sm uppercase bg-black text-white px-2 py-1 mb-2">Work Required</h2>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left py-1 w-8">#</th>
                  <th className="text-left py-1">Description</th>
                  <th className="text-center py-1 w-20">Est. Hrs</th>
                  <th className="text-center py-1 w-24">Status</th>
                  <th className="text-center py-1 w-16">Done</th>
                </tr>
              </thead>
              <tbody>
                {ro.workItems.map((item, i) => (
                  <tr key={item.id} className="border-b border-gray-300">
                    <td className="py-2">{i + 1}</td>
                    <td className="py-2">{item.description}</td>
                    <td className="py-2 text-center">{item.estimatedHours || '-'}</td>
                    <td className="py-2 text-center uppercase text-xs">{item.status}</td>
                    <td className="py-2 text-center">
                      <span className="inline-block w-4 h-4 border-2 border-black"></span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Parts Section */}
        {parts.length > 0 && (
          <div className="mb-4">
            <h2 className="font-bold text-sm uppercase bg-black text-white px-2 py-1 mb-2">Parts Needed</h2>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left py-1">Part</th>
                  <th className="text-left py-1 w-24">Part #</th>
                  <th className="text-center py-1 w-12">Qty</th>
                  <th className="text-left py-1 w-28">Vendor</th>
                  <th className="text-center py-1 w-24">Status</th>
                  <th className="text-center py-1 w-16">Recv'd</th>
                </tr>
              </thead>
              <tbody>
                {parts.map((part) => (
                  <tr key={part.id} className="border-b border-gray-300">
                    <td className="py-2">{part.description}</td>
                    <td className="py-2 font-mono text-xs">{part.partNumber || '-'}</td>
                    <td className="py-2 text-center">{part.quantity || 1}</td>
                    <td className="py-2 text-xs">{part.vendorName || '-'}</td>
                    <td className="py-2 text-center uppercase text-xs">{part.status}</td>
                    <td className="py-2 text-center">
                      <span className="inline-block w-4 h-4 border-2 border-black"></span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Outside Vendors Section */}
        {ro.outsideVendors?.length > 0 && (
          <div className="mb-4">
            <h2 className="font-bold text-sm uppercase bg-black text-white px-2 py-1 mb-2">Outside Vendors</h2>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left py-1">Type</th>
                  <th className="text-left py-1">Vendor</th>
                  <th className="text-center py-1 w-24">Status</th>
                  <th className="text-center py-1 w-16">Done</th>
                </tr>
              </thead>
              <tbody>
                {ro.outsideVendors.map((v) => (
                  <tr key={v.id} className="border-b border-gray-300">
                    <td className="py-2 uppercase">{v.vendorType}</td>
                    <td className="py-2">{v.vendorName || '-'}</td>
                    <td className="py-2 text-center uppercase text-xs">{v.status}</td>
                    <td className="py-2 text-center">
                      <span className="inline-block w-4 h-4 border-2 border-black"></span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes Section */}
        {ro.notes && (
          <div className="mb-4">
            <h2 className="font-bold text-sm uppercase bg-black text-white px-2 py-1 mb-2">Notes</h2>
            <div className="border border-gray-300 p-2 text-sm min-h-16">{ro.notes}</div>
          </div>
        )}

        {/* Game Plan Note Section */}
        {ro.gamePlanNote && (
          <div className="mb-4">
            <h2 className="font-bold text-sm uppercase bg-blue-600 text-white px-2 py-1 mb-2">Game Plan</h2>
            <div className="border border-gray-300 p-2 text-sm min-h-16 bg-blue-50">{ro.gamePlanNote}</div>
          </div>
        )}

        {/* ============ TOTALS SECTION FOR PRINT ============ */}
        <div className="mb-4 mt-6">
          <h2 className="font-bold text-sm uppercase bg-black text-white px-2 py-1 mb-2">Cost Summary</h2>
          <div className="border border-black">
            <div className="flex justify-between px-3 py-1 border-b border-gray-300 text-sm">
              <span>Parts ({parts.length} items)</span>
              <span className="font-mono">${partsCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between px-3 py-1 border-b border-gray-300 text-sm">
              <span>Labor ({totalLaborHours.toFixed(1)} hrs)</span>
              <span className="font-mono">${laborCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between px-3 py-1 border-b border-gray-300 text-sm">
              <span>Materials</span>
              <span className="font-mono">${materialsCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between px-3 py-1 border-b border-gray-300 text-sm">
              <span>Outside Services</span>
              <span className="font-mono">${vendorsCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between px-3 py-2 bg-black text-white font-bold">
              <span>TOTAL</span>
              <span className="font-mono">${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="mt-8 pt-4 border-t-2 border-black">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <div className="border-b border-black mb-1 h-8"></div>
              <div className="text-xs text-gray-600">Technician Signature / Date</div>
            </div>
            <div>
              <div className="border-b border-black mb-1 h-8"></div>
              <div className="text-xs text-gray-600">Manager Approval / Date</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-2 border-t border-gray-300 text-xs text-gray-500 flex justify-between">
          <span>Printed: {new Date().toLocaleString()}</span>
          <span>LotSense Work Order</span>
        </div>
      </div>

      {/* ============ APPROVAL BANNER ============ */}
      {requiresApproval && showCosts && (
        <ApprovalBanner
          ro={ro}
          grandTotal={grandTotal}
          onRefresh={loadRO}
          isAdmin={isAdmin}
        />
      )}

      {/* ============ ROW 1: Vehicle Info + Auction Said/QR — side by side ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print:hidden">
        {/* BOX 1: Vehicle Info */}
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Vehicle Info</div>
          <div className="grid grid-cols-3 gap-x-3 gap-y-1.5 text-xs">
            <div><span className="text-neutral-400">Stock#</span> <span className="font-mono font-bold text-sm">{ro.stockNumber || '-'}</span></div>
            <div className="col-span-2"><span className="text-neutral-400">Vehicle</span> <span className="font-semibold text-sm">{ro.vehicleYear} {ro.vehicleMake} {ro.vehicleModel}</span></div>
            <div className="col-span-2"><span className="text-neutral-400">VIN</span> <VinDisplay vin={ro.vin} /></div>
            <div><span className="text-neutral-400">Miles</span> <span className="font-mono">{ro.mileage ? ro.mileage.toLocaleString() : '-'}</span></div>
            <div><span className="text-neutral-400">RO#</span> <span className="font-mono font-medium">{ro.roNumber}</span></div>
            <div><span className="text-neutral-400">Opened</span> <span>{new Date(ro.createdAt).toLocaleDateString()}</span></div>
            <div>
              <span className="text-neutral-400">Tech</span>
              <select value={ro.assignedToUserId || ''} onChange={e => updateRO({ assignedToUserId: e.target.value ? parseInt(e.target.value) : null })} className="ml-1 text-xs px-1 py-0.5 border border-neutral-200 rounded bg-white">
                <option value="">--</option>
                {shopUsers.map(u => (<option key={u.id} value={u.id}>{u.name}</option>))}
              </select>
            </div>
            <div>
              <span className="text-neutral-400">Status</span>
              <select value={ro.status} onChange={e => updateRO({ status: e.target.value })} className="ml-1 text-xs px-1 py-0.5 border border-neutral-200 rounded bg-white">
                {RO_STATUSES.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
              </select>
            </div>
            <div>
              <span className="text-neutral-400">Priority</span>
              <select value={ro.priority || 'normal'} onChange={e => updateRO({ priority: e.target.value })} className="ml-1 text-xs px-1 py-0.5 border border-neutral-200 rounded bg-white">
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        </div>

        {/* BOX 2: Auction Said + QR Code */}
        <div className="bg-white border border-neutral-200 rounded-lg p-3 flex gap-3">
          <div className="flex-1">
            <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Auction Said</div>
            {(() => {
              const lights = (ro.vehicle?.auctionLight || '').split(',').filter(Boolean);
              const hasGreen = lights.includes('green');
              const hasYellow = lights.includes('yellow');
              const hasRed = lights.includes('red');
              return (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">
                    {hasGreen && '🟢'}{hasYellow && '🟡'}{hasRed && '🔴'}{lights.length === 0 && '⚪'}
                  </span>
                  <div>
                    <div className={`text-xs font-bold ${hasRed ? 'text-red-700' : hasYellow ? 'text-yellow-700' : hasGreen ? 'text-green-700' : 'text-neutral-500'}`}>
                      {lights.length === 0 ? 'NOT SET' : lights.map(l => l.toUpperCase()).join(' + ')}
                    </div>
                    {ro.vehicle?.auctionAnnouncements && (
                      <div className="text-xs text-neutral-600 mt-0.5">{ro.vehicle.auctionAnnouncements}</div>
                    )}
                  </div>
                </div>
              );
            })()}
            {ro.vehicle?.endUse === 'dealer_direct' && (
              <div className="p-2 bg-red-100 border border-red-300 rounded text-xs text-red-800 font-bold">⚠️ DEALER DIRECT — THOROUGH INSPECTION REQUIRED</div>
            )}
          </div>
          {/* QR Code */}
          <div className="flex flex-col items-center justify-center border-l border-neutral-200 pl-3">
            <QRCodeSVG value={`https://lotsense.thewholesalecars.com/car/${ro.stockNumber}`} size={64} level="M" />
            <div className="text-xs text-neutral-400 mt-1">Scan</div>
          </div>
        </div>
      </div>

      {/* ============ ROW 2: What It Needs + Tech Assessment — side by side ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print:hidden">
        {/* BOX 3: What It Needs (Complaint) */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-1">What It Needs</div>
          <div className="text-sm">{ro.complaint || <span className="text-neutral-400 italic">No complaint entered</span>}</div>
          {ro.findings && (
            <div className="mt-2 pt-2 border-t border-yellow-200">
              <div className="text-xs text-yellow-600 font-medium mb-0.5">Inspection Notes:</div>
              <div className="text-xs whitespace-pre-wrap">{ro.findings}</div>
            </div>
          )}
        </div>

        {/* BOX 4: Tech Assessment */}
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Tech Assessment</div>
          <div className="flex gap-1.5 mb-2">
            {[
              { value: 'green', label: '🟢 Good', color: 'bg-green-100 border-green-500 text-green-700' },
              { value: 'yellow', label: '🟡 Issues', color: 'bg-yellow-100 border-yellow-500 text-yellow-700' },
              { value: 'red', label: '🔴 Major', color: 'bg-red-100 border-red-500 text-red-700' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={async () => {
                  if (ro.vehicleId) {
                    await api.updateVehicle(ro.vehicleId, { inspectionLight: opt.value });
                    loadRO();
                  }
                }}
                className={`flex-1 py-1.5 text-xs font-medium rounded border-2 transition ${
                  ro.vehicle?.inspectionLight === opt.value ? opt.color : 'bg-neutral-50 border-neutral-200 text-neutral-500 hover:bg-neutral-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <textarea
            value={ro.vehicle?.inspectionLightNotes || ''}
            onChange={async (e) => { if (ro.vehicleId) await api.updateVehicle(ro.vehicleId, { inspectionLightNotes: e.target.value }); }}
            onBlur={loadRO}
            placeholder="Tech notes on condition..."
            className="w-full h-14 px-2 py-1 text-xs border border-neutral-200 rounded resize-none"
          />
          {/* Arbitration Alert */}
          {(() => {
            const auctionLights = (ro.vehicle?.auctionLight || '').split(',');
            const auctionHadGreen = auctionLights.includes('green');
            const techFoundIssues = ro.vehicle?.inspectionLight === 'yellow' || ro.vehicle?.inspectionLight === 'red';
            if (auctionHadGreen && techFoundIssues) {
              return (
                <button onClick={async () => {
                  if (!window.confirm('Flag this vehicle for arbitration?')) return;
                  try {
                    await api.updateVehicle(ro.vehicleId, { arbStatus: 'flagged', inArbitration: true });
                    await api.createTask({ title: `🚨 ARB: ${ro.stockNumber} - Green Light has issues`, description: `Tech found ${ro.vehicle?.inspectionLight === 'red' ? 'MAJOR' : 'ISSUES'}. Notes: ${ro.vehicle?.inspectionLightNotes || 'None'}`, assignedRole: 'admin', priority: 'urgent', vehicleId: ro.vehicleId });
                    alert('Flagged for arbitration!'); loadRO();
                  } catch (err) { alert('Failed: ' + err.message); }
                }} className="mt-2 w-full py-1.5 bg-red-600 text-white text-xs font-bold rounded hover:bg-red-700">🚨 FLAG FOR ARBITRATION</button>
              );
            }
            return null;
          })()}
        </div>
      </div>

      {/* ============ ROW 3: Compact Upload Strip ============ */}
      <div className="bg-white border border-neutral-200 rounded-lg p-2 flex items-center gap-2 flex-wrap print:hidden">
        <span className="text-xs font-semibold text-neutral-500 uppercase">Uploads:</span>
        <FileUpload accept="video/*" onUpload={(url) => handleUpload('video', url)} className="inline-flex items-center gap-1 px-2.5 py-1 border border-neutral-200 rounded text-xs hover:bg-blue-50 hover:border-blue-300 transition cursor-pointer">
          <span>🎥</span> {ro.inspectionVideoUrl ? <a href={ro.inspectionVideoUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>Video ✓</a> : <span className="text-neutral-500">Video</span>}
        </FileUpload>
        <FileUpload accept="image/*,.pdf" onUpload={(url) => handleUpload('scan', url)} className="inline-flex items-center gap-1 px-2.5 py-1 border border-neutral-200 rounded text-xs hover:bg-blue-50 hover:border-blue-300 transition cursor-pointer">
          <span>🔍</span> {ro.bluedriverScanUrl ? <a href={ro.bluedriverScanUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>Scan ✓</a> : <span className="text-neutral-500">Scan</span>}
        </FileUpload>
        <FileUpload accept="image/*" multiple onUpload={(url) => handleUpload('photo', url)} className="inline-flex items-center gap-1 px-2.5 py-1 border border-neutral-200 rounded text-xs hover:bg-blue-50 hover:border-blue-300 transition cursor-pointer">
          <span>📷</span> <span className="text-neutral-500">Photos ({ro.photos?.length || 0})</span>
        </FileUpload>
        {ro.photos?.length > 0 && (
          <div className="flex-1 flex gap-1 overflow-x-auto ml-1">
            <PhotoGallery photos={ro.photos} onDelete={handlePhotoDelete} editable={true} />
          </div>
        )}
      </div>

      {/* Section C (WorkItemsSection) removed - labor now handled via Additional Labor section at bottom */}

      {/* ============ ESTIMATE ONLY / PROCEEDING STATUS ============ */}
      {!ro.isCustomerVehicle && (
        <div className="bg-white border-2 border-orange-300 rounded-lg p-3 mb-2">
          <div className="flex items-start gap-3">
            <div className="flex items-center gap-3 flex-1">
              <input
                type="checkbox"
                id="estimateOnlyToggle"
                checked={!!ro.estimateOnly}
                onChange={async (e) => {
                  const newValue = e.target.checked;
                  try {
                    // Use dedicated endpoint (avoids autosave/reload loops)
                    await handleToggleEstimateOnly(newValue);
                  } catch (error) {
                    alert('Failed to save estimate only status. Please try again.');
                  }
                }}
                className="w-5 h-5 text-orange-600 border-orange-300 rounded focus:ring-orange-500 focus:ring-2 cursor-pointer"
              />
              <div className="flex-1">
                <label htmlFor="estimateOnlyToggle" className="cursor-pointer">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-base font-bold ${ro.estimateOnly ? 'text-orange-700' : 'text-green-700'}`}>
                      {ro.estimateOnly ? '☐ Estimate Only' : '☑ Proceeding with Repairs'}
                    </span>
                  </div>
                  <div className="text-sm text-neutral-600">
                    {ro.estimateOnly ? (
                      <span>Parts list for review, no order placed. <strong className="text-orange-700">Does NOT affect vehicle price.</strong></span>
                    ) : (
                      <span>Parts ordered/approved. <strong className="text-green-700">DOES affect vehicle price.</strong></span>
                    )}
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ SECTION D: Parts Needed ============ */}
      <div className="bg-white border border-neutral-200 rounded-lg p-3">
        {/* Parts Ordering Links */}
        <div className="flex items-center gap-1.5 flex-wrap mb-2 pb-2 border-b border-neutral-100 print:hidden">
          <span className="text-xs font-semibold text-neutral-500">Order Parts:</span>
          <a href="https://www.car-part.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-orange-500 text-white rounded text-xs font-bold hover:bg-orange-600">Car-Part</a>
          <a href="https://www.repairlinkshop.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-green-600 text-white rounded text-xs font-bold hover:bg-green-700">RepairLink</a>
          <a href="https://www.ebay.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-yellow-400 text-black rounded text-xs font-bold hover:bg-yellow-300">eBay</a>
          <a href="https://www.amazon.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700">Amazon</a>
          <a href="https://www.rockauto.com" target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-neutral-700 text-white rounded text-xs font-bold hover:bg-neutral-600">RockAuto</a>
        </div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <SectionHeader letter="D" title="Parts Needed" />
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleToggleEstimateOnly(!ro.estimateOnly)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                  ro.estimateOnly
                    ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200'
                    : 'bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100'
                }`}
                title="When ON: quote mode (does not hit RO totals/recon). When OFF: committed mode."
              >
                Estimate Only: {ro.estimateOnly ? 'ON' : 'OFF'}
              </button>
              {ro.estimateOnly && (
                <span className="text-xs text-amber-700">
                  Quote mode: totals shown are estimates until committed.
                </span>
              )}
              {ro.estimateOnly && showCosts && (
                <span className="text-xs text-neutral-600">
                  Committed Total: <strong>${committedGrandTotal.toFixed(2)}</strong>
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Show Cancelled toggle */}
            <label className="flex items-center gap-1 text-xs text-neutral-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showCancelledParts}
                onChange={(e) => setShowCancelledParts(e.target.checked)}
                className="w-3 h-3"
              />
              Show Cancelled
            </label>
            <button
              onClick={handleSyncPrecheckParts}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Sync Precheck Parts
            </button>
            <button
              onClick={() => setShowAddPart(true)}
              className="px-3 py-1 text-xs bg-neutral-800 text-white rounded hover:bg-neutral-700"
            >
              + Add Part
            </button>
          </div>
        </div>

        {/* Add Part Form */}
        {showAddPart && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="col-span-2 md:col-span-1">
                <label className="block text-sm font-medium mb-1">Part Name *</label>
                <input
                  type="text"
                  value={newPart.description}
                  onChange={e => setNewPart(p => ({ ...p, description: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                  placeholder="e.g., Brake Pads"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Part Number</label>
                <input
                  type="text"
                  value={newPart.partNumber}
                  onChange={e => setNewPart(p => ({ ...p, partNumber: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={newPart.quantity}
                  onChange={e => setNewPart(p => ({ ...p, quantity: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vendor/Source</label>
                <input
                  type="text"
                  value={newPart.vendorName}
                  onChange={e => setNewPart(p => ({ ...p, vendorName: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                  placeholder="e.g., AutoZone"
                />
              </div>
              {showCosts && (
                <div>
                  <label className="block text-sm font-medium mb-1">Unit Cost $</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newPart.unitCost}
                    onChange={e => setNewPart(p => ({ ...p, unitCost: e.target.value }))}
                    className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                    placeholder="0.00"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={newPart.status}
                  onChange={e => setNewPart(p => ({ ...p, status: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                >
                  <option value="needed">Needed</option>
                  <option value="ordered">Ordered</option>
                  <option value="backordered">Backordered</option>
                  <option value="received">Received</option>
                  <option value="installed">Installed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date Ordered</label>
                <input
                  type="date"
                  value={newPart.orderDate}
                  onChange={e => setNewPart(p => ({ ...p, orderDate: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Est. Delivery</label>
                <input
                  type="date"
                  value={newPart.eta}
                  onChange={e => setNewPart(p => ({ ...p, eta: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                  title="Check the parts room when this date arrives!"
                />
                <div className="text-xs text-amber-600 mt-1"> Check parts room on this date</div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="block text-sm font-medium mb-1">Paid From Account</label>
                <select
                  value={newPart.bankAccountId}
                  onChange={e => setNewPart(p => ({ ...p, bankAccountId: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg bg-white"
                >
                  <option value="">Select account...</option>
                  {bankAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2 md:col-span-3">
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={newPart.notes}
                  onChange={e => setNewPart(p => ({ ...p, notes: e.target.value }))}
                  className="w-full h-16 px-3 py-2 border border-neutral-200 rounded-lg resize-none"
                  placeholder="Optional notes about this part..."
                />
              </div>
            </div>

            {/* Labor & Materials Section */}
            <div className="mt-4 pt-4 border-t border-green-200">
              <h4 className="text-sm font-semibold text-green-800 mb-3">⚙️ Labor & Materials for this Part/Job</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Labor Hours</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={newPart.laborHours}
                    onChange={e => setNewPart(p => ({ ...p, laborHours: e.target.value }))}
                    className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                    placeholder="0"
                  />
                </div>
                {showCosts && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Labor Rate $/hr</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={newPart.laborRate}
                      onChange={e => setNewPart(p => ({ ...p, laborRate: e.target.value }))}
                      className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                      placeholder="35"
                    />
                  </div>
                )}
                {showCosts && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Materials Cost $</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newPart.materialsCost}
                      onChange={e => setNewPart(p => ({ ...p, materialsCost: e.target.value }))}
                      className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                )}
                <div className={showCosts ? '' : 'col-span-2'}>
                  <label className="block text-sm font-medium mb-1">Materials Description</label>
                  <input
                    type="text"
                    value={newPart.materialsDescription}
                    onChange={e => setNewPart(p => ({ ...p, materialsDescription: e.target.value }))}
                    className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                    placeholder="e.g., Paint, supplies"
                  />
                </div>
              </div>
              {showCosts && (newPart.laborHours || newPart.materialsCost) && (
                <div className="mt-2 text-sm text-green-700">
                  Labor: ${((parseFloat(newPart.laborHours) || 0) * (parseFloat(newPart.laborRate) || 35)).toFixed(2)} + 
                  Materials: ${(parseFloat(newPart.materialsCost) || 0).toFixed(2)} = 
                  <strong> ${(((parseFloat(newPart.laborHours) || 0) * (parseFloat(newPart.laborRate) || 35)) + (parseFloat(newPart.materialsCost) || 0)).toFixed(2)}</strong>
                </div>
              )}
              <div className="mt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newPart.isBundledJob}
                    onChange={e => setNewPart(p => ({ ...p, isBundledJob: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm">This is a bundled job (labor covers multiple parts)</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 mt-3 border-t">
              <button
                onClick={() => { setShowAddPart(false); setNewPart({ description: '', partNumber: '', quantity: 1, unitCost: '', vendorName: '', status: 'ordered', orderDate: new Date().toISOString().split('T')[0], eta: '', bankAccountId: '', notes: '', laborHours: '', laborRate: '35', materialsCost: '', materialsDescription: '', isBundledJob: false }); }}
                className="px-3 py-1.5 text-neutral-600 hover:bg-neutral-100 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPart}
                disabled={addingPart}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
              >
                {addingPart ? 'Adding...' : 'Add Part'}
              </button>
            </div>
          </div>
        )}

        {/* Parts List - Editable */}
        {parts.length > 0 ? (
          <div className="space-y-2">
            {parts
              .filter(part => showCancelledParts || !part.cancelled)
              .map((part) => (
              <div 
                key={part.id} 
                className={`p-3 rounded-lg ${
                  part._pending ? 'bg-blue-50 border border-blue-200' :
                  part.cancelled 
                    ? 'bg-red-50 border border-red-200 opacity-60' 
                    : 'bg-neutral-50'
                }`}
              >
                {/* Pending save indicator */}
                {part._pending && (
                  <div className="text-xs text-blue-600 mb-2 font-medium">Saving...</div>
                )}
                {/* Cancelled banner */}
                {part.cancelled && !part._pending && (
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-red-200">
                    <span className="text-xs font-semibold text-red-700 line-through">
                      CANCELLED {part.cancelledAt ? `on ${new Date(part.cancelledAt).toLocaleDateString()}` : ''}
                      {part.cancelledReason && ` - ${part.cancelledReason}`}
                    </span>
                    <button
                      onClick={() => handleRestorePart(part.id)}
                      className="px-2 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Restore
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-12 gap-2 items-start">
                  {/* Part Name & Number */}
                  <div className="col-span-3">
                    <input
                      type="text"
                      className={`w-full px-2 py-1 text-sm font-medium border border-neutral-200 rounded ${part.cancelled ? 'line-through text-neutral-400' : ''}`}
                      defaultValue={part.description || ''}
                      onBlur={e => {
                        if (!part._pending && e.target.value !== part.description) {
                          handleUpdatePart(part.id, { description: e.target.value });
                        }
                      }}
                      placeholder="Part name"
                      disabled={part.cancelled || part._pending}
                    />
                    <input
                      type="text"
                      className="w-full px-2 py-1 text-xs border border-neutral-200 rounded mt-1"
                      defaultValue={part.partNumber || ''}
                      onBlur={e => {
                        if (!part._pending && e.target.value !== (part.partNumber || '')) {
                          handleUpdatePart(part.id, { partNumber: e.target.value });
                        }
                      }}
                      placeholder="Part # (optional)"
                      disabled={part.cancelled || part._pending}
                    />
                  </div>
                  {/* Quantity */}
                  <div className="col-span-1">
                    <label className="text-xs text-neutral-500">Qty</label>
                    <input
                      type="number"
                      min="1"
                      className="w-full px-2 py-1 text-xs border border-neutral-200 rounded"
                      defaultValue={part.quantity || 1}
                      onBlur={e => {
                        if (part._pending) return;
                        const val = parseInt(e.target.value) || 1;
                        if (val !== part.quantity) {
                          handleUpdatePart(part.id, { quantity: val });
                        }
                      }}
                      disabled={part.cancelled || part._pending}
                    />
                  </div>
                  {/* Vendor */}
                  <div className="col-span-2">
                    <label className="text-xs text-neutral-500">Vendor</label>
                    <input
                      type="text"
                      className="w-full px-2 py-1 text-xs border border-neutral-200 rounded"
                      defaultValue={part.vendorName || ''}
                      onBlur={e => {
                        if (e.target.value !== (part.vendorName || '')) {
                          handleUpdatePart(part.id, { vendorName: e.target.value });
                        }
                      }}
                      placeholder="Vendor"
                    />
                  </div>
                  {/* ETA */}
                  <div className="col-span-2">
                    <label className="text-xs text-neutral-500">ETA</label>
                    <input
                      type="date"
                      className="w-full px-2 py-1 text-xs border border-neutral-200 rounded"
                      defaultValue={part.eta?.split('T')[0] || ''}
                      onChange={e => handleUpdatePart(part.id, { eta: e.target.value })}
                    />
                  </div>
                  {/* Status */}
                  <div className="col-span-2">
                    <label className="text-xs text-neutral-500">Status</label>
                    <select
                      className="w-full px-2 py-1 text-xs border border-neutral-200 rounded bg-white"
                      value={part.status || 'needed'}
                      onChange={e => handleUpdatePart(part.id, { status: e.target.value })}
                    >
                      <option value="needed">Needed</option>
                      <option value="ordered">Ordered</option>
                      <option value="backordered">Backordered</option>
                      <option value="shipped">Shipped</option>
                      <option value="arrived">Arrived</option>
                      <option value="installed">Installed</option>
                    </select>
                  </div>
                  {/* Actions */}
                  <div className="col-span-2 flex items-center gap-1 pt-4">
                    <button
                      onClick={() => {
                        const reminderDate = prompt('Set reminder date (YYYY-MM-DD):', part.eta?.split('T')[0] || new Date().toISOString().split('T')[0]);
                        if (reminderDate) {
                          api.createTask({
                            vehicleId: ro.vehicleId,
                            title: `📦 Part Reminder: ${part.description}`,
                            description: `Check on part for RO ${ro.roNumber}:\n- Part: ${part.description}\n- Vendor: ${part.vendorName || 'N/A'}`,
                            dueDate: reminderDate,
                            assignedRole: 'shop',
                            priority: 'normal',
                            status: 'pending'
                          }).then(() => alert(`Reminder set for ${reminderDate}`)).catch(err => alert('Failed: ' + err.message));
                        }
                      }}
                      className="text-blue-500 hover:text-blue-700 text-sm"
                      title="Set Reminder"
                    >
                      🔔
                    </button>
                    {!part.cancelled && (
                      <button
                        onClick={() => !part._pending && handleCancelPart(part.id, part.description)}
                        disabled={part._pending}
                        className="text-orange-500 hover:text-orange-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Cancel part (keeps info, removes from costs)"
                      >
                        ⊘
                      </button>
                    )}
                    <button
                      onClick={() => !part._pending && handleDeletePart(part.id)}
                      disabled={part._pending}
                      className="text-red-500 hover:text-red-700 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Permanently delete"
                    >
                      ×
                    </button>
                  </div>
                </div>
                {/* Notes row */}
                <div className="mt-2">
                  <input
                    type="text"
                    className="w-full px-2 py-1 text-xs border border-neutral-200 rounded"
                    defaultValue={part.notes || ''}
                    onBlur={e => {
                      if (e.target.value !== (part.notes || '')) {
                        handleUpdatePart(part.id, { notes: e.target.value });
                      }
                    }}
                    placeholder="Notes (optional)"
                  />
                </div>
                {/* Labor & Materials row */}
                <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <span className="text-neutral-500">Labor Hrs:</span>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      className="w-16 px-2 py-1 border border-neutral-200 rounded"
                      defaultValue={part.laborHours || ''}
                      onBlur={e => {
                        const val = parseFloat(e.target.value) || 0;
                        if (val !== parseFloat(part.laborHours || 0)) {
                          handleUpdatePart(part.id, { laborHours: val });
                        }
                      }}
                      placeholder="0"
                    />
                  </div>
                  {showCosts && (
                    <div className="flex items-center gap-1">
                      <span className="text-neutral-500">Rate $:</span>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        className="w-16 px-2 py-1 border border-neutral-200 rounded"
                        defaultValue={part.laborRate || 35}
                        onBlur={e => {
                          const val = parseFloat(e.target.value) || 35;
                          if (val !== parseFloat(part.laborRate || 35)) {
                            handleUpdatePart(part.id, { laborRate: val });
                          }
                        }}
                      />
                    </div>
                  )}
                  {showCosts && (
                    <div className="flex items-center gap-1">
                      <span className="text-neutral-500">Materials $:</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-20 px-2 py-1 border border-neutral-200 rounded"
                        defaultValue={part.materialsCost || ''}
                        onBlur={e => {
                          const val = parseFloat(e.target.value) || 0;
                          if (val !== parseFloat(part.materialsCost || 0)) {
                            handleUpdatePart(part.id, { materialsCost: val });
                          }
                        }}
                        placeholder="0"
                      />
                    </div>
                  )}
                  <div className="col-span-2 flex items-center gap-1">
                    <span className="text-neutral-500">Mat. Desc:</span>
                    <input
                      type="text"
                      className="flex-1 px-2 py-1 border border-neutral-200 rounded"
                      defaultValue={part.materialsDescription || ''}
                      onBlur={e => {
                        if (e.target.value !== (part.materialsDescription || '')) {
                          handleUpdatePart(part.id, { materialsDescription: e.target.value });
                        }
                      }}
                      placeholder="Paint, supplies, etc."
                    />
                  </div>
                </div>
                {/* Cost row - only for admins */}
                {showCosts && (
                  <div className="mt-2 flex items-center gap-4 text-xs border-t pt-2">
                    <div className="flex items-center gap-1">
                      <span className="text-neutral-500">Part $</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-20 px-2 py-1 border border-neutral-200 rounded"
                        defaultValue={part.unitCost || ''}
                        onBlur={e => {
                          const val = parseFloat(e.target.value) || 0;
                          if (val !== parseFloat(part.unitCost || 0)) {
                            handleUpdatePart(part.id, { unitCost: val });
                          }
                        }}
                      />
                    </div>
                    <span className="text-neutral-500">
                      Parts: ${parseFloat(part.totalCost || 0).toFixed(2)} | 
                      Labor: ${parseFloat(part.laborCost || 0).toFixed(2)} | 
                      Materials: ${parseFloat(part.materialsCost || 0).toFixed(2)}
                    </span>
                    <span className="font-semibold text-green-600">
                      Line Total: ${(parseFloat(part.totalCost || 0) + parseFloat(part.laborCost || 0) + parseFloat(part.materialsCost || 0)).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-neutral-400">
            <div className="text-2xl mb-2"></div>
            <div className="text-sm">No parts added yet</div>
            <div className="text-xs">Click "Sync Precheck Parts" or "Add Part" to get started</div>
          </div>
        )}
      </div>

      {/* ============ SECTION E: Outside Vendors - Combined Checkboxes + Details ============ */}
      <CombinedOutsideVendors ro={ro} onUpdate={updateRO} onRefresh={loadRO} showCosts={showCosts} />

      {/* ============ SECTION F: Costs & Approval (Admin Only) ============ */}
      {showCosts && (
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <SectionHeader letter="F" title="Costs & Approval" />
          
          <div className="grid grid-cols-5 gap-2 text-center">
            <div className="bg-neutral-50 rounded p-2">
              <div className="text-xs text-neutral-500">Parts</div>
              <div className="text-sm font-mono font-bold">${partsCost.toFixed(2)}</div>
            </div>
            <div className="bg-neutral-50 rounded p-2">
              <div className="text-xs text-neutral-500">Labor ({totalLaborHours.toFixed(1)}h)</div>
              <div className="text-sm font-mono font-bold">${laborCost.toFixed(2)}</div>
            </div>
            <div className="bg-neutral-50 rounded p-2">
              <div className="text-xs text-neutral-500">Materials</div>
              <div className="text-sm font-mono font-bold">${materialsCost.toFixed(2)}</div>
            </div>
            <div className="bg-neutral-50 rounded p-2">
              <div className="text-xs text-neutral-500">Vendors</div>
              <div className="text-sm font-mono font-bold">${vendorsCost.toFixed(2)}</div>
            </div>
            <div className={`rounded p-2 ${requiresApproval ? 'bg-orange-100' : 'bg-green-100'}`}>
              <div className="text-xs text-neutral-500">Total</div>
              <div className="text-base font-mono font-bold">${grandTotal.toFixed(2)}</div>
              {requiresApproval && <div className="text-xs text-orange-600">Over $600</div>}
            </div>
          </div>
        </div>
      )}

      {/* ============ ADDITIONAL LABOR SECTION ============ */}
      <div className="bg-white border border-neutral-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 class="font-semibold text-neutral-800 text-sm">➕ Additional Labor</h3>
          <button
            onClick={() => setShowAddLabor(!showAddLabor)}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            {showAddLabor ? 'Cancel' : '+ Add Labor'}
          </button>
        </div>
        
        <p className="text-xs text-neutral-500 mb-3">
          Add labor time not associated with a specific part (e.g., diagnostic time, test drives, general repairs)
        </p>

        {showAddLabor && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs text-neutral-600 mb-1">Description *</label>
                <input
                  type="text"
                  value={newLabor.description}
                  onChange={e => setNewLabor(l => ({ ...l, description: e.target.value }))}
                  placeholder="e.g., Diagnostic time, Test drive, General repair"
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-neutral-600 mb-1">Hours *</label>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={newLabor.hours}
                  onChange={e => setNewLabor(l => ({ ...l, hours: e.target.value }))}
                  placeholder="1.0"
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={handleAddLabor}
                disabled={addingLabor}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
              >
                {addingLabor ? 'Adding...' : 'Add Labor Entry'}
              </button>
            </div>
          </div>
        )}

        {/* Show existing labor-only entries */}
        {parts.filter(p => p.description?.startsWith('[LABOR]')).length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-neutral-500 font-medium">Labor Entries:</div>
            {parts.filter(p => p.description?.startsWith('[LABOR]')).map(labor => (
              <div key={labor.id} className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium">{labor.description.replace('[LABOR] ', '')}</span>
                  <span className="text-xs text-neutral-500 ml-2">({labor.laborHours || 0}h)</span>
                </div>
                <button
                  onClick={() => handleDeletePart(labor.id)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ============ SECTION G: Notes ============ */}
      <div className="bg-white border border-neutral-200 rounded-lg p-3">
        <SectionHeader letter="G" title="Notes" />
        
        <textarea
          defaultValue={ro.notes || ''}
          onBlur={e => {
            if (e.target.value !== (ro.notes || '')) {
              updateRO({ notes: e.target.value });
            }
          }}
          className="w-full h-20 px-2 py-1.5 border border-neutral-200 rounded text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="General notes about this repair order..."
        />
      </div>

      {/* ============ SECTION H: Timeline/History ============ */}
      <TimelineSection ro={ro} />

      {/* ============ REPAIR ORDER TOTALS - SHOP STYLE ============ */}
      <div className="bg-white border-2 border-black rounded-lg p-4 mt-2">
        <div className="text-center border-b-2 border-black pb-2 mb-3">
          <h2 className="text-base font-bold uppercase tracking-wide">Repair Order Summary</h2>
          <div className="text-xs text-neutral-500">RO# {ro.roNumber}</div>
        </div>

        {/* Line Items Breakdown */}
        <div className="space-y-1 text-sm font-mono mb-4">
          {/* Parts */}
          <div className="flex justify-between py-1 border-b border-dashed border-neutral-300">
            <span>PARTS ({ro.parts?.length || 0} items)</span>
            <span>${partsCost.toFixed(2)}</span>
          </div>
          
          {/* Labor */}
          <div className="flex justify-between py-1 border-b border-dashed border-neutral-300">
            <span>LABOR ({totalLaborHours.toFixed(1)} hrs @ $35/hr)</span>
            <span>${laborCost.toFixed(2)}</span>
          </div>
          
          {/* Materials */}
          <div className="flex justify-between py-1 border-b border-dashed border-neutral-300">
            <span>MATERIALS</span>
            <span>${materialsCost.toFixed(2)}</span>
          </div>
          
          {/* Outside Vendors */}
          <div className="flex justify-between py-1 border-b border-dashed border-neutral-300">
            <span>OUTSIDE SERVICES</span>
            <span>${vendorsCost.toFixed(2)}</span>
          </div>
        </div>

        {/* Subtotal */}
        <div className="flex justify-between py-2 text-sm font-mono border-t border-black">
          <span className="font-bold">SUBTOTAL</span>
          <span className="font-bold">${grandTotal.toFixed(2)}</span>
        </div>

        {/* Tax line (placeholder - $0) */}
        <div className="flex justify-between py-1 text-sm font-mono text-neutral-500">
          <span>TAX</span>
          <span>$0.00</span>
        </div>

        {/* Grand Total */}
        <div className="flex justify-between py-3 mt-2 bg-black text-white px-4 -mx-4 text-lg font-bold">
          <span>TOTAL DUE</span>
          <span>${grandTotal.toFixed(2)}</span>
        </div>

        {/* Signature Lines */}
        <div className="grid grid-cols-2 gap-8 mt-8 pt-4">
          <div>
            <div className="border-b border-black mb-1 h-8"></div>
            <div className="text-xs text-neutral-500">Customer Signature</div>
          </div>
          <div>
            <div className="border-b border-black mb-1 h-8"></div>
            <div className="text-xs text-neutral-500">Date</div>
          </div>
        </div>

        {/* Shop Info Footer */}
        <div className="text-center mt-6 pt-4 border-t border-neutral-200 text-xs text-neutral-500">
          <div className="font-medium">Thank you for your business!</div>
          <div>All work guaranteed • Payment due upon completion</div>
        </div>
      </div>

      {/* Bottom padding for scroll */}
      <div className="h-8"></div>
    </div>
  );
};

// ============ APPROVAL BANNER COMPONENT ============
const ApprovalBanner = ({ ro, grandTotal, onRefresh, isAdmin }) => {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitForApproval = async () => {
    setSubmitting(true);
    try {
      await api.submitROForApproval(ro.id);
      onRefresh();
    } catch (err) {
      alert('Failed to submit: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await api.approveRO(ro.id);
      
      // Send notification to recon team
      try {
        await api.createNotification({
          title: `RO #${ro.roNumber} Approved`,
          message: `Work order for ${ro.stockNumber} (${ro.vehicleYear} ${ro.vehicleMake} ${ro.vehicleModel}) has been approved. Total: $${grandTotal.toFixed(2)}`,
          type: 'approval',
          vehicleId: ro.vehicleId,
          roId: ro.id,
          recipients: ['recon_team'] // Will be expanded to actual user IDs
        });
      } catch (notifErr) {
        console.warn('Failed to send approval notification:', notifErr);
      }
      
      alert(` RO #${ro.roNumber} approved! Recon team has been notified.`);
      onRefresh();
    } catch (err) {
      alert('Failed to approve: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    const reason = prompt('Please enter the reason for denial:');
    if (!reason) return;
    
    setSubmitting(true);
    try {
      await api.denyRO(ro.id, reason);
      
      // Send notification to recon team
      try {
        await api.createNotification({
          title: `RO #${ro.roNumber} Denied`,
          message: `Work order for ${ro.stockNumber} (${ro.vehicleYear} ${ro.vehicleMake} ${ro.vehicleModel}) has been denied. Reason: ${reason}`,
          type: 'denial',
          vehicleId: ro.vehicleId,
          roId: ro.id,
          recipients: ['recon_team'] // Will be expanded to actual user IDs
        });
      } catch (notifErr) {
        console.warn('Failed to send denial notification:', notifErr);
      }
      
      alert(` RO #${ro.roNumber} denied. Recon team has been notified.`);
      onRefresh();
    } catch (err) {
      alert('Failed to deny: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Determine banner color based on status
  let bgColor = 'bg-orange-100 border-orange-300';
  let statusText = 'REQUIRES APPROVAL (Over $600)';
  
  if (ro.approvalStatus === 'approved') {
    bgColor = 'bg-green-100 border-green-300';
    statusText = 'APPROVED';
  } else if (ro.approvalStatus === 'denied') {
    bgColor = 'bg-red-100 border-red-300';
    statusText = 'DENIED';
  } else if (ro.approvalStatus === 'pending') {
    bgColor = 'bg-yellow-100 border-yellow-300';
    statusText = 'PENDING APPROVAL';
  }

  return (
    <div className={`${bgColor} border-2 rounded-xl p-4 print:hidden`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="text-3xl">
            {ro.approvalStatus === 'approved' ? '' : ro.approvalStatus === 'denied' ? '' : ''}
          </span>
          <div>
            <div className="font-bold text-lg">
              Total: ${grandTotal.toFixed(2)} - {statusText}
            </div>
            {ro.approvedByName && (
              <div className="text-sm">
                By {ro.approvedByName} on {new Date(ro.approvedAt).toLocaleDateString()}
              </div>
            )}
            {ro.approvalNotes && (
              <div className="text-sm">Note: {ro.approvalNotes}</div>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          {/* Not yet submitted */}
          {!ro.approvalStatus && (
            <button
              onClick={handleSubmitForApproval}
              disabled={submitting}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit for Approval'}
            </button>
          )}
          
          {/* Pending - show approve/deny buttons for admin */}
          {ro.approvalStatus === 'pending' && isAdmin && (
            <>
              <button
                onClick={handleApprove}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {submitting ? '...' : 'Approve'}
              </button>
              <button
                onClick={handleDeny}
                disabled={submitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {submitting ? '...' : 'Deny'}
              </button>
            </>
          )}
          
          {/* Pending - show waiting message for non-admin */}
          {ro.approvalStatus === 'pending' && !isAdmin && (
            <span className="px-4 py-2 bg-yellow-200 text-yellow-800 rounded-lg font-medium">
              Waiting for Admin Approval
            </span>
          )}
        </div>
      </div>

    </div>
  );
};

// ============ COMBINED OUTSIDE VENDORS - Checkboxes + Details + Complete/Notes ============
const CHECKBOX_TO_VENDOR_TYPE = {
  pdr: 'pdr',
  body_outside: 'body_shop_outside',
  body_inhouse: 'body_shop_inhouse',
  rim_wheel: 'rim_repair',
  interior: 'interior_repair',
  glass: 'glass',
  detail: 'detail',
  mechanical_outside: 'mechanical_outside',
  other: 'other'
};
const VENDOR_TYPE_TO_CHECKBOX = Object.fromEntries(
  Object.entries(CHECKBOX_TO_VENDOR_TYPE).map(([k, v]) => [v, k])
);

const VENDOR_OPTIONS = [
  { id: 'pdr', label: 'PDR' },
  { id: 'body_outside', label: 'Body - Outside' },
  { id: 'body_inhouse', label: 'Body - In House' },
  { id: 'rim_wheel', label: 'Rim/Wheel' },
  { id: 'interior', label: 'Interior' },
  { id: 'glass', label: 'Glass' },
  { id: 'detail', label: 'Detail' },
  { id: 'mechanical_outside', label: 'Mechanical - Outside' },
  { id: 'other', label: 'Other' }
];

const CombinedOutsideVendors = ({ ro, onUpdate, onRefresh, showCosts }) => {
  const [expandedAdd, setExpandedAdd] = useState(null);
  const [addForm, setAddForm] = useState({ vendorName: '', estimatedCost: '', scheduledDate: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [completeModal, setCompleteModal] = useState({ show: false, vendor: null, actualCost: '', notes: '' });
  const [removeModal, setRemoveModal] = useState({ show: false, vendor: null, notes: '' });

  const outsideVendors = ro?.outsideVendors || [];
  const getVendorForType = (vendorType) => outsideVendors.find(v => v.vendorType === vendorType);
  const isChecked = (opt) => !!getVendorForType(CHECKBOX_TO_VENDOR_TYPE[opt.id]);

  const syncVendorCheckboxes = useCallback((vendors) => {
    const boxes = {};
    vendors.forEach(v => {
      const key = VENDOR_TYPE_TO_CHECKBOX[v.vendorType];
      if (key) boxes[key] = { checked: true, note: v.notes || '' };
    });
    onUpdate({ vendorCheckboxes: JSON.stringify(boxes) });
  }, [onUpdate]);

  const handleCheck = (opt) => {
    if (isChecked(opt)) return;
    setExpandedAdd(opt.id);
    setAddForm({ vendorName: '', estimatedCost: '', scheduledDate: '', notes: '' });
  };

  const handleAddVendor = async (opt) => {
    if (!ro?.id) return;
    setSaving(true);
    try {
      await api.addROVendor(ro.id, {
        vendorType: CHECKBOX_TO_VENDOR_TYPE[opt.id],
        vendorName: addForm.vendorName || null,
        estimatedCost: parseFloat(addForm.estimatedCost) || 0,
        scheduledDate: addForm.scheduledDate || null,
        notes: addForm.notes || null
      });
      setExpandedAdd(null);
      setAddForm({ vendorName: '', estimatedCost: '', scheduledDate: '', notes: '' });
      const updated = await api.getRepairOrder(ro.id);
      syncVendorCheckboxes(updated?.outsideVendors || []);
      onRefresh();
    } catch (err) {
      alert('Failed to add vendor: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkComplete = (vendor) => {
    const suggested = vendor.actualCost > 0 ? vendor.actualCost : (vendor.estimatedCost || 0);
    setCompleteModal({ show: true, vendor, actualCost: suggested ? String(suggested) : '', notes: vendor.notes || '' });
  };

  const handleSubmitComplete = async () => {
    if (!completeModal.vendor) return;
    try {
      await api.updateROVendor(ro.id, completeModal.vendor.id, {
        status: 'complete',
        actualCost: parseFloat(completeModal.actualCost) || 0,
        notes: completeModal.notes || completeModal.vendor.notes
      });
      setCompleteModal({ show: false, vendor: null, actualCost: '', notes: '' });
      onRefresh();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  const handleUncheck = (vendor) => {
    setRemoveModal({ show: true, vendor, notes: '' });
  };

  const handleSubmitRemove = async () => {
    if (!removeModal.vendor) return;
    try {
      await api.deleteROVendor(ro.id, removeModal.vendor.id);
      const updated = await api.getRepairOrder(ro.id);
      syncVendorCheckboxes(updated?.outsideVendors || []);
      setRemoveModal({ show: false, vendor: null, notes: '' });
      onRefresh();
    } catch (err) {
      alert('Failed to remove: ' + err.message);
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4">
      <SectionHeader letter="E" title="Outside Vendors" />
      
      <div className="space-y-3">
        {VENDOR_OPTIONS.map(opt => {
          const vendor = getVendorForType(CHECKBOX_TO_VENDOR_TYPE[opt.id]);
          const checked = !!vendor;
          const isAdding = expandedAdd === opt.id;

          return (
            <div key={opt.id} className="border border-neutral-200 rounded-lg overflow-hidden">
              <div
                className={`flex items-center justify-between px-4 py-2 cursor-pointer transition ${
                  checked ? 'bg-purple-100 border-purple-200' : 'bg-white hover:bg-neutral-50'
                } ${vendor?.status === 'complete' ? 'bg-green-50 border-green-200' : ''}`}
                onClick={() => !checked && !isAdding && handleCheck(opt)}
              >
                <label className="flex items-center gap-2 cursor-pointer flex-1" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {}}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (checked) {
                        handleUncheck(vendor);
                      } else {
                        handleCheck(opt);
                      }
                    }}
                    className="w-4 h-4 rounded border-neutral-300 text-purple-600 cursor-pointer"
                  />
                  <span className="font-medium">{opt.label}</span>
                  {vendor?.vendorName && <span className="text-sm text-neutral-500">• {vendor.vendorName}</span>}
                  {vendor?.status === 'complete' && (
                    <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded ml-2">Complete</span>
                  )}
                  {checked && showCosts && (vendor.actualCost > 0 || vendor.estimatedCost > 0) && (
                    <span className="text-sm font-medium text-green-600 ml-auto">
                      ${parseFloat(vendor.actualCost || vendor.estimatedCost || 0).toFixed(2)}
                      {vendor.status === 'complete' && vendor.actualCost > 0 && <span className="text-neutral-500"> (actual)</span>}
                    </span>
                  )}
                </label>
                {checked && !isAdding && (
                  <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                    {vendor.status !== 'complete' && (
                      <button
                        onClick={() => handleMarkComplete(vendor)}
                        className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Mark Complete
                      </button>
                    )}
                    <button
                      onClick={() => handleUncheck(vendor)}
                      className="text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      Uncheck
                    </button>
                  </div>
                )}
              </div>

              {isAdding && (
                <div className="bg-purple-50 border-t border-purple-200 p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 mb-1">Vendor Name</label>
                      <input
                        value={addForm.vendorName}
                        onChange={e => setAddForm(f => ({ ...f, vendorName: e.target.value }))}
                        placeholder="Optional"
                        className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                    </div>
                    {showCosts && (
                      <div>
                        <label className="block text-xs font-medium text-neutral-600 mb-1">Est. Cost $</label>
                        <input
                          type="number"
                          step="0.01"
                          value={addForm.estimatedCost}
                          onChange={e => setAddForm(f => ({ ...f, estimatedCost: e.target.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 mb-1">Scheduled Date</label>
                      <input
                        type="date"
                        value={addForm.scheduledDate}
                        onChange={e => setAddForm(f => ({ ...f, scheduledDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">Notes / Work Description</label>
                    <textarea
                      value={addForm.notes}
                      onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Describe the work needed..."
                      className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm resize-none"
                      rows={2}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setExpandedAdd(null)} className="px-3 py-1.5 text-neutral-600 hover:bg-neutral-100 rounded text-sm">Cancel</button>
                    <button
                      onClick={() => handleAddVendor(opt)}
                      disabled={saving}
                      className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                    >
                      {saving ? 'Adding...' : 'Add Vendor'}
                    </button>
                  </div>
                </div>
              )}

              {vendor && vendor.notes && !isAdding && (
                <div className="px-4 py-2 bg-neutral-50 border-t border-neutral-100 text-sm text-neutral-600">
                  📝 {vendor.notes}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showCosts && outsideVendors.length > 0 && (
        <div className="mt-4 pt-3 border-t flex justify-end text-sm">
          <span>Total: <strong className="text-green-600">$
            {outsideVendors.reduce((s, v) => s + parseFloat(v.actualCost || v.estimatedCost || 0), 0).toFixed(2)}
          </strong></span>
        </div>
      )}

      {/* Mark Complete Modal */}
      {completeModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setCompleteModal({ show: false, vendor: null, actualCost: '', notes: '' })}>
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl p-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-lg mb-3">Mark Complete</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Actual cost ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={completeModal.actualCost}
                  onChange={e => setCompleteModal(m => ({ ...m, actualCost: e.target.value }))}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={completeModal.notes}
                  onChange={e => setCompleteModal(m => ({ ...m, notes: e.target.value }))}
                  placeholder="Optional completion notes..."
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg resize-none"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setCompleteModal({ show: false, vendor: null, actualCost: '', notes: '' })} className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg">Cancel</button>
              <button onClick={handleSubmitComplete} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      {/* Uncheck/Remove Modal */}
      {removeModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setRemoveModal({ show: false, vendor: null, notes: '' })}>
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl p-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-lg mb-3">Remove Vendor</h3>
            <p className="text-sm text-neutral-500 mb-3">Add a note for why you're removing (optional):</p>
            <textarea
              value={removeModal.notes}
              onChange={e => setRemoveModal(m => ({ ...m, notes: e.target.value }))}
              placeholder="e.g., Cancelled, done in-house..."
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg resize-none"
              rows={3}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRemoveModal({ show: false, vendor: null, notes: '' })} className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg">Cancel</button>
              <button onClick={handleSubmitRemove} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RODetailPage;
